import { Trans } from "@lingui/macro";
import { FC, useCallback, useRef, useState } from "react";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { ProductVariation, SingleProduct } from "src/types/graphql.d";
import {
  useSizeSchemaFilter,
  SizeMapping,
} from "src/hooks/useSizeSchemaFilter";

import {
  SizeSelectorContainer,
  SizeSelectorButton,
  FloatingLabel,
  SelectedSize,
  BrandSize,
} from "./SizeSelectorDropdown.styled";
import SizeSelectorPanel from "./SizeSelectorPanel";

export type { SizeMapping };

interface SizeSelectorDropdownProps {
  variations: ProductVariation[];
  selectedVariationId?: string;
  onSelectVariation: (variationId: string) => void;
  hasError?: boolean;
  disabled?: boolean;
  locale?: string;
  getStockText?: (quantity: number) => React.ReactNode | null;
  "data-testid"?: string;
  onMobileClick?: () => void;
  selectedSchema?: string;
  onSchemaChange?: (schema: string) => void;
  product?: SingleProduct;
  onBackInStockPopupChange?: (isOpen: boolean) => void;
}

const SizeSelectorDropdown: FC<SizeSelectorDropdownProps> = ({
  variations,
  selectedVariationId,
  onSelectVariation,
  hasError = false,
  disabled = false,
  locale,
  getStockText,
  "data-testid": dataTestId = "size-selector-dropdown",
  onMobileClick,
  selectedSchema: externalSchema,
  onSchemaChange,
  product,
  onBackInStockPopupChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const {
    sizeMappings,
    selectedSchema,
    handleSelectSchema,
    formatBrandSize,
    formatTranslatedSize,
  } = useSizeSchemaFilter({
    variations,
    locale,
    selectedSchema: externalSchema,
    onSchemaChange,
  });

  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  const selectedSize = sizeMappings.find(
    (mapping) => mapping.variationId === selectedVariationId
  );

  let brandSizeText = "";
  let translatedSizeText = "";
  const hasSelection = !!selectedSize;

  if (selectedSize) {
    brandSizeText = formatBrandSize(selectedSize);

    translatedSizeText = formatTranslatedSize(selectedSize, true);
  }

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;

    if (onMobileClick) {
      onMobileClick();
      return;
    }

    setIsOpen((prev) => !prev);
  }, [disabled, onMobileClick]);

  const handleSelectVariation = (variationId: string) => {
    onSelectVariation(variationId);
    setIsOpen(false);
  };

  return (
    <SizeSelectorContainer ref={wrapperRef} data-testid={dataTestId}>
      <SizeSelectorButton
        type="button"
        isOpen={isOpen}
        hasError={hasError}
        hasValue={hasSelection}
        onClick={handleToggleDropdown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <FloatingLabel isFloating={hasSelection || isOpen}>
          <Trans>Select your size</Trans>
        </FloatingLabel>
        {hasSelection && (
          <SelectedSize
            key={`${selectedVariationId || brandSizeText}-${selectedSchema}`}
            $isOpen={isOpen}
          >
            {translatedSizeText ? (
              <>
                <BrandSize>{brandSizeText}</BrandSize>
                {translatedSizeText}
              </>
            ) : (
              brandSizeText
            )}
          </SelectedSize>
        )}
      </SizeSelectorButton>

      <SizeSelectorPanel
        isOpen={isOpen}
        variations={variations}
        selectedVariationId={selectedVariationId}
        onSelectVariation={handleSelectVariation}
        locale={locale}
        getStockText={getStockText}
        selectedSchema={selectedSchema}
        onSchemaChange={handleSelectSchema}
        product={product}
        onBackInStockPopupChange={onBackInStockPopupChange}
      />
    </SizeSelectorContainer>
  );
};

export default SizeSelectorDropdown;
